'use client';

import { CONTENT_TYPES, MESSAGE_STATUS } from '@repo/utils';
import type { Message } from '@repo/api';
import { MessageBubble } from './MessageBubble';
import { AgentContactCard } from './AgentContactCard';
import { ListingsCarousel } from './ListingsCarousel';
import { RetryMessage } from './RetryMessage';
import { TransitionMessage } from './TransitionMessage';
import { MultipleChoiceCard } from './MultipleChoiceCard';
import { MediaCard } from './MediaCard';
import { DocumentRequestCard } from './DocumentRequestCard';
import { DocumentViewCard } from './DocumentViewCard';

interface ChatMessageProps {
  message: Message;
  isGrouped?: boolean;
  isFirst?: boolean;
  isOverlay?: boolean;
  onSlotSelect: (value: string, message: Message) => void;
  onRetry?: (message: Message) => void;
  onUrlClick?: (url: string) => void;
}

export function ChatMessage({
  message,
  isGrouped,
  isFirst,
  isOverlay,
  onSlotSelect,
  onRetry,
  onUrlClick,
}: ChatMessageProps) {
  const renderContent = () => {
    switch (message.contentType) {
      case CONTENT_TYPES.TEXT:
        return (
          <>
            {message.contentMessage && (
              <MessageBubble
                message={message}
                isGrouped={isGrouped}
                {...(onUrlClick ? { onUrlClick } : {})}
              />
            )}
            {message.status === MESSAGE_STATUS.FAILED && onRetry && (
              <RetryMessage
                errorMessage="Message not delivered."
                onRetry={() => onRetry(message)}
              />
            )}
          </>
        );

      case CONTENT_TYPES.MULTIPLE_CHOICE:
        return (
          <>
            {message.contentData && (
              <MultipleChoiceCard message={message} onSlotSelect={onSlotSelect} />
            )}
            {message.status === MESSAGE_STATUS.FAILED && onRetry && (
              <RetryMessage
                errorMessage="Selection not delivered."
                onRetry={() => onRetry(message)}
              />
            )}
          </>
        );

      case CONTENT_TYPES.INFO_CARD:
        return <>{message.contentData && <AgentContactCard message={message} />}</>;

      case CONTENT_TYPES.LISTINGS:
        return (
          <>
            {message.contentData && (
              <div className={`${!isOverlay ? 'md:mx-3 -mx-6' : 'md:mx-0 -mx-6'}`}>
                <ListingsCarousel message={message} isMessageWindow={true} />
              </div>
            )}
          </>
        );

      case CONTENT_TYPES.LOADING:
        return <MessageBubble message={message} />;

      case CONTENT_TYPES.TRANSITION:
        return (
          <TransitionMessage
            label={typeof message.contentMessage === 'string' ? message.contentMessage : null}
          />
        );

      case CONTENT_TYPES.MEDIA:
        return <>{message.contentData && <MediaCard message={message} />}</>;

      case CONTENT_TYPES.DOCUMENT_REQUEST:
        return <DocumentRequestCard message={message} />;

      case CONTENT_TYPES.DOCUMENT_VIEW:
        return <DocumentViewCard message={message} />;

      default:
        return null;
    }
  };

  const isTransition = message.contentType === CONTENT_TYPES.TRANSITION;
  const marginTop = isFirst || isTransition ? '' : isGrouped ? 'mt-2' : 'mt-6';

  return <div className={`w-full ${marginTop}`}>{renderContent()}</div>;
}
